import React, { useId } from 'react';

const TONE_COLORS = {
  query: { stroke: '#6366f1', fill: 'rgba(99, 102, 241, 0.18)' },
  payload: { stroke: '#14b8a6', fill: 'rgba(20, 184, 166, 0.18)' },
  tokens: { stroke: '#8b5cf6', fill: 'rgba(139, 92, 246, 0.18)' },
  default: { stroke: '#6366f1', fill: 'rgba(99, 102, 241, 0.18)' },
};

function formatXLabel(point) {
  if (!point) return '';
  const ts = point.ts || point.t || point.timestamp || point.day || point.x;
  if (!ts) return '';
  try {
    const d = ts instanceof Date ? ts : new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return String(ts);
  }
}

function formatNumber(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (!isFinite(num)) return '—';
  if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000) return `${(num / 1_000).toFixed(1)}k`;
  return num.toLocaleString();
}

/**
 * UsageChart - polished SVG line chart with grid, axis labels, and tooltips
 * via SVG <title>. No external chart library.
 *
 * Props:
 *   data       Array<{ts|day|x: any, y: number}>
 *   tone       'query' | 'payload' | 'tokens' (default: 'query')
 *   yLabel     string  unit shown next to tooltips (e.g., "queries")
 *   height     number  in px (default 200)
 */
export default function UsageChart({ data, tone = 'query', yLabel = '', height = 200 }) {
  const colors = TONE_COLORS[tone] || TONE_COLORS.default;
  const idSuffix = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const series = Array.isArray(data) ? data.filter((d) => d != null) : [];
  if (series.length === 0) {
    return (
      <div
        className="billing-admin__chart-svg"
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--uh-text-muted)',
          fontSize: 'var(--uh-font-size-sm)',
          background: 'var(--uh-bg-main)',
          borderRadius: 8,
        }}
      >
        No usage data for this period yet.
      </div>
    );
  }

  const W = 800;
  const H = height;
  const padL = 44;
  const padR = 12;
  const padT = 8;
  const padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const ys = series.map((p) => Number(p.y ?? p.value ?? 0)).map((v) => (isFinite(v) ? v : 0));
  const maxY = Math.max(1, ...ys);
  const minY = 0;

  const stepX = series.length > 1 ? innerW / (series.length - 1) : 0;
  const points = series.map((p, i) => {
    const v = Number(p.y ?? p.value ?? 0) || 0;
    const x = padL + i * stepX;
    const y = padT + innerH - ((v - minY) / (maxY - minY || 1)) * innerH;
    return { x, y, raw: p, v };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${(points[points.length - 1].x).toFixed(1)},${padT + innerH} L${(points[0].x).toFixed(1)},${padT + innerH} Z`;

  // 4 horizontal gridlines including 0 and max
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((t) => {
    const y = padT + innerH - t * innerH;
    const v = minY + (maxY - minY) * t;
    return { y, v };
  });

  const xTickIndexes = (() => {
    const desired = Math.min(6, series.length);
    if (desired <= 1) return [0];
    const step = (series.length - 1) / (desired - 1);
    const arr = [];
    for (let i = 0; i < desired; i++) arr.push(Math.round(i * step));
    return arr;
  })();

  return (
    <svg
      className="billing-admin__chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Usage chart${yLabel ? ` (${yLabel})` : ''}`}
    >
      <defs>
        <linearGradient id={`ba-grad-${idSuffix}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.stroke} stopOpacity="0.32" />
          <stop offset="100%" stopColor={colors.stroke} stopOpacity="0" />
        </linearGradient>
      </defs>

      <g className="billing-admin__chart-grid">
        {gridLines.map((g, i) => (
          <line key={i} x1={padL} x2={W - padR} y1={g.y} y2={g.y} />
        ))}
      </g>

      <g className="billing-admin__chart-axis">
        {gridLines.map((g, i) => (
          <text key={`y-${i}`} x={padL - 6} y={g.y + 4} textAnchor="end">
            {formatNumber(g.v)}
          </text>
        ))}
        {xTickIndexes.map((idx) => {
          if (!points[idx]) return null;
          return (
            <text key={`x-${idx}`} x={points[idx].x} y={H - 6} textAnchor="middle">
              {formatXLabel(points[idx].raw)}
            </text>
          );
        })}
      </g>

      <path d={areaPath} fill={`url(#ba-grad-${idSuffix})`} />
      <path d={linePath} fill="none" stroke={colors.stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

      <g>
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={3} fill={colors.stroke}>
              <title>{`${formatXLabel(p.raw)}: ${formatNumber(p.v)}${yLabel ? ` ${yLabel}` : ''}`}</title>
            </circle>
          </g>
        ))}
      </g>
    </svg>
  );
}
